"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { Users, Activity, Globe } from "lucide-react"

// ==================== Counter ====================
const Counter = ({ end, suffix = "", inView }: { end: number; suffix?: string; inView: boolean }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!inView) {
      setCount(0)
      return
    }

    const duration = 2600
    const startTime = performance.now()
    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4)

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutQuart(progress)
      const value = end * eased

      if (Number.isInteger(end)) setCount(Math.floor(value))
      else setCount(Number(value.toFixed(1)))

      if (progress < 1) requestAnimationFrame(animate)
    }

    requestAnimationFrame(animate)
  }, [inView, end])

  return (
    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-cyan-300 tracking-tight tabular-nums">
      {count}
      {suffix && <span className="text-cyan-300 ml-1 text-xl sm:text-2xl">{suffix}</span>}
    </div>
  )
}

// ==================== About Section ====================
const About = () => {
  const [statsRef, statsInView] = useInView({
    threshold: 0.4,
    triggerOnce: false,
  })

  return (
    <section
      id="about"
      className="relative flex items-center min-h-screen justify-center py-20 sm:py-24 md:py-32 overflow-hidden px-safe"
    >
      <div className="absolute inset-0 mesh-gradient opacity-70" />

      <div className="relative z-10 w-full">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-16">
          <div className="flex flex-col lg:flex-row justify-between items-center text-center lg:text-left gap-12 lg:gap-20 xl:gap-24">

            {/* النص */}
            <div className="flex-1 max-w-2xl">

            <motion.h1
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.8 }}
  className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-300 leading-relaxed"
>
  Transforming healthcare claims management across the GCC through cutting-edge artificial intelligence
  and deep regional expertise.
</motion.h1>


              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="mt-6 text-sm sm:text-base md:text-lg leading-relaxed text-gray-300"
              >
                Kayan Healthcare Technologies emerged in 2019 with a bold mission: redefine e-claims across the GCC
                through <span className="text-cyan-400 font-semibold">uncompromising speed</span>,{" "}
                <span className="text-blue-400 font-semibold">intelligence</span>, and{" "}
                <span className="text-cyan-300 font-semibold">precision</span>.
                <br />
                <br />
                Our roots in the UAE fueled rapid expansion into Oman, with{" "}
                <span className="font-semibold">Saudi Arabia</span> and <span className="font-semibold">Qatar</span>.
                Through <span className="text-purple-400 font-semibold">PETRA</span> and{" "}
                <span className="text-purple-400 font-semibold">QUDRA</span>, we deliver instant accuracy, unwavering
                compliance, and elite AI performance.
                <br />
                <br />
                Kayan stands as a driving force in digital healthcare transformation—
                <span className="text-cyan-300 font-bold"> relentless in innovation</span>,{" "}
                <span className="text-blue-400 font-bold"> firm in purpose</span>,{" "}
                <span className="text-cyan-400 font-bold"> fully committed</span>.
              </motion.p>

              {/* الإحصائيات */}
              <div
                ref={statsRef}
                className="mt-8 flex flex-col sm:flex-row justify-center lg:justify-start gap-6 lg:gap-10 text-center"
              >

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={statsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col items-center"
                >
                  <Users size={22} className="text-cyan-400 mb-2" />
                  <Counter end={200} suffix="+" inView={statsInView} />
                  <p className="mt-1 text-xs text-gray-400 uppercase tracking-wider font-medium">Clients Served</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={statsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex flex-col items-center"
                >
                  <Activity size={22} className="text-emerald-400 mb-2" />
                  <Counter end={99.9} suffix="%" inView={statsInView} />
                  <p className="mt-1 text-xs text-gray-400 uppercase tracking-wider font-medium">System Uptime</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={statsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="flex flex-col items-center col-span-2 sm:col-auto"
                >
                  <Globe size={22} className="text-purple-400 mb-2" />
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    MENA
                  </div>
                  <p className="mt-1 text-xs text-gray-400 uppercase tracking-wider font-medium">Regional Coverage</p>
                </motion.div>

              </div>
            </div>

            {/* الصورة */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="w-full max-w-[280px] sm:max-w-[350px] lg:max-w-md self-center"
            >
              <div className="relative aspect-[4/5] sm:aspect-square rounded-[2rem] overflow-hidden shadow-2xl shadow-cyan-500/20">

                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 via-blue-500/20 to-purple-500/30 p-1">
                  <div className="w-full h-full rounded-[1.8rem] overflow-hidden">
                    <img
                      src="/ChatGPT Image Feb 20, 2026, 05_49_04 PM.png"
                      alt="Healthcare AI 3D"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="absolute inset-0 rounded-[2rem] border-2 border-cyan-400/20 pointer-events-none" />

              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  )
}

export default About